import { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useAIStore } from '../../stores/aiStore';
import { aiService } from '../../services/ai.service';
import { toast } from 'sonner';

/**
 * Confirmation modal for destructive AI tool calls (roadmap #48 PR-B).
 *
 * The chat loop has paused on the server side; we have at most `timeoutMs`
 * to send a confirm/cancel ack. If the user dismisses or the timer expires,
 * we send cancel as a courtesy — the server will time out anyway.
 */
export function DestructiveToolModal() {
  const pendingTool = useAIStore((s) => s.pendingTool);
  const clearPendingTool = useAIStore((s) => s.clearPendingTool);
  const [busy, setBusy] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  // Live countdown reflects server-side timeout
  useEffect(() => {
    if (!pendingTool) return;
    const update = () => {
      const elapsed = Date.now() - pendingTool.receivedAt;
      const remaining = Math.max(0, Math.ceil((pendingTool.timeoutMs - elapsed) / 1000));
      setSecondsLeft(remaining);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [pendingTool]);

  if (!pendingTool) return null;

  const handle = async (confirm: boolean) => {
    if (busy) return;
    setBusy(true);
    const ok = await aiService.confirmTool(pendingTool.correlationId, confirm);
    setBusy(false);
    clearPendingTool();
    if (!ok) {
      toast.warning('Onay zaten kapanmıştı (zaman aşımı veya başka bir tarayıcı sekmesi).');
    } else if (!confirm) {
      toast.info(`"${pendingTool.name}" iptal edildi.`);
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg rounded-lg border border-amber-500/30 bg-white shadow-2xl dark:bg-gray-900">
        <div className="flex items-center gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <AlertTriangle className="h-6 w-6 flex-shrink-0 text-amber-500" />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Yıkıcı araç onayı gerekli
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              AI bir destructive tool çağırmak istiyor. Onaylamadan çalıştırılmaz.
            </p>
          </div>
          <button
            type="button"
            onClick={() => handle(false)}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
            aria-label="İptal et"
            disabled={busy}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Tool</div>
            <div className="font-mono text-sm text-gray-900 dark:text-gray-100">{pendingTool.name}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">Parametreler</div>
            <pre className="mt-1 max-h-60 overflow-auto rounded bg-gray-100 p-2 text-xs font-mono text-gray-800 dark:bg-gray-800 dark:text-gray-200">
{JSON.stringify(pendingTool.args, null, 2)}
            </pre>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>
              Otomatik iptal:{' '}
              <span className={secondsLeft <= 5 ? 'font-semibold text-red-500' : ''}>
                {secondsLeft}s
              </span>
            </span>
            <span className="rounded bg-amber-500/10 px-2 py-0.5 text-amber-700 dark:text-amber-300">
              {pendingTool.dangerLevel}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3 dark:border-gray-700 dark:bg-gray-800/50">
          <button
            type="button"
            onClick={() => handle(false)}
            disabled={busy}
            className="rounded border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={() => handle(true)}
            disabled={busy}
            className="rounded bg-amber-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            Evet, çalıştır
          </button>
        </div>
      </div>
    </div>
  );
}
